import { Shield, FileText, Cookie, Network, Settings } from 'lucide-react';
import { LegalHeader } from '@/legal/components/LegalHeader';
import { CeoIntro } from '@/legal/components/CeoIntro';
import { DocumentCard } from '@/legal/components/DocumentCard';

type Jurisdiction = 'ru' | 'eu' | 'us';

interface DocItem {
  slug: string;
  href: string;
  title: string;
  description: string;
  readingMinutes: number;
  icon: typeof Shield;
  jurisdictions: Jurisdiction[];
}

const DOCUMENTS: DocItem[] = [
  {
    slug: 'privacy',
    href: '/legal/privacy',
    title: 'Политика конфиденциальности',
    description:
      'Какие данные мы собираем, зачем, кому передаём. Как ты управляешь своими данными.',
    readingMinutes: 18,
    icon: Shield,
    jurisdictions: ['ru', 'eu', 'us'],
  },
  {
    slug: 'terms',
    href: '/legal/terms',
    title: 'Условия использования',
    description:
      'Правила пользования AgentCEO. Что можно, что нельзя, как мы решаем споры.',
    readingMinutes: 22,
    icon: FileText,
    jurisdictions: ['ru', 'eu', 'us'],
  },
  {
    slug: 'cookie',
    href: '/legal/cookie',
    title: 'Файлы cookie',
    description:
      'Какие cookies используем, зачем, как управлять. Опт-аут одним кликом.',
    readingMinutes: 7,
    icon: Cookie,
    jurisdictions: ['ru', 'eu', 'us'],
  },
  {
    slug: 'subprocessors',
    href: '/legal/subprocessors',
    title: 'Sub-processors',
    description:
      'Список компаний, которым мы передаём ограниченный доступ к данным (Anthropic, Cloudflare и др.).',
    readingMinutes: 5,
    icon: Network,
    jurisdictions: ['ru', 'eu', 'us'],
  },
];

/**
 * Index-страница /legal — каталог всех правовых документов.
 * Spec: docs/superpowers/specs/2026-05-09-legal-index-page-design.md
 */
export function LegalIndexPage() {
  return (
    <div className="min-h-screen bg-surface-canvas">
      <LegalHeader title="" />
      <article className="mx-auto max-w-4xl px-4 pb-16 pt-8 md:px-8 md:pt-10">
        <header className="space-y-3">
          <h1 className="font-display text-3xl font-bold text-text-primary md:text-4xl lg:text-5xl">
            Правовые документы
          </h1>
          <p className="text-base leading-relaxed text-text-secondary md:text-lg">
            Документы, регулирующие использование AgentCEO. Действуют с 2026-05-07.
          </p>
        </header>

        <div className="mt-8">
          <CeoIntro />
        </div>

        <div className="mt-8 grid grid-cols-1 gap-6 md:grid-cols-2">
          {DOCUMENTS.map((doc) => (
            <DocumentCard
              key={doc.slug}
              title={doc.title}
              description={doc.description}
              href={doc.href}
              readingMinutes={doc.readingMinutes}
              icon={doc.icon}
            />
          ))}
        </div>

        <aside className="mt-8 rounded-lg border border-border bg-surface-subtle p-4 text-center text-sm text-text-secondary">
          Все документы действуют для пользователей в России, ЕС и США.
        </aside>

        <aside className="mt-12 border-t border-border pt-6">
          <a
            href="/account/privacy/cookies"
            className="inline-flex items-center gap-2 text-sm text-text-secondary hover:text-action-primary-bg focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-border-focus/40 rounded transition-colors"
          >
            <Settings className="h-4 w-4" aria-hidden />
            Управлять файлами cookie →
          </a>
        </aside>
      </article>
    </div>
  );
}
